import { LitElement, html, css, nothing, unsafeCSS } from "lit";
import { property, customElement, state } from "lit/decorators.js";
import { when } from "lit/directives/when.js";
import { classMap } from "lit/directives/class-map.js";
import { styleMap } from "lit/directives/style-map.js";
import { resolveColor } from "../helper/utils/color-resolver";
import { cssOnFillEnhance } from "../helper/utils/contrast-color";
import { FormControlBase, FormState } from "../helper/internals/form-control-base";
import { PresetController } from "../helper/controllers/preset-controller";
import { skyFocusRingHostStyles } from "../helper/styles/focus-ring";

export interface CheckboxProperties {
  label: string;
  checked: boolean;
  disabled: boolean;
  indeterminate: boolean;
  id: string;
  value: string;
  color: string;
  required: boolean;
  validationErrors: string[];
  validationActive: boolean;
  defaultChecked: boolean;
  requiredMessage: string;
  showErrors: boolean;
  prefix: string;
  suffix: string;
  prefixColor: string;
  suffixColor: string;
}
/** `checked` is always the boolean model; `value` keeps form/legacy semantics (see `handleChange`). */
export type SkyCheckboxValueChangedDetail = {
  checked: boolean;
  value: string | boolean | undefined;
};
export type SkyCheckboxValidationErrorDetail = { errors: string[] };
export type SkyCheckboxValueClearedDetail = { value: string; checked: boolean };

/**
 * @element sky-checkbox
 *
 * @summary Form-associated checkbox with validation, indeterminate state, and customizable label/prefix/suffix rendering.
 *
 * @status stable
 * @since 1.0.0
 *
 * @documentation https://sky-ui.com/components/checkbox
 *
 * @uiVModel checked value-changed detail=checked
 *
 * @slot - Default slot for the checkbox label or other content.
 * @slot prefix - Optional prefix content that can be placed before the label.
 * @slot suffix - Optional suffix content that can be placed after the label.
 *
 * @csspart wrapper - The wrapper element for the checkbox.
 * @csspart input - The native input element used for the checkbox.
 * @csspart label - The label element for the checkbox, used for styling.
 * @csspart box - The visual box for the checkbox, which includes the checkmark and indeterminate state.
 * @csspart checkmark - The checkmark icon that appears when the checkbox is checked.
 * @csspart indeterminate-line - The line representing the indeterminate state of the checkbox.
 * @csspart error-message - The error message displayed if the checkbox is invalid.
 *
 * @property {string} label - The label for the checkbox. Default: `""`.
 * @property {boolean} checked - Whether the checkbox is checked. Default: `false`.
 * @property {boolean} indeterminate - Whether the checkbox is in the indeterminate state. Default: `false`.
 * @property {string} color - The color used for the checkbox and its checkmark. Default: `"primary"`.
 * @property {string} value - The value of the checkbox, which gets submitted with the form. Default: `""`.
 * @property {boolean} showErrors - Whether to display validation error messages. Default: `false`.
 * @property {string} prefix - Optional prefix text or content before the label. Default: `""`.
 * @property {string} suffix - Optional suffix text or content after the label. Default: `""`.
 * @property {string} prefixColor - The color for the prefix text. Default: `""`.
 * @property {string} suffixColor - The color for the suffix text. Default: `""`.
 * @property {boolean} defaultChecked - Default checked state for reset operations. Default: `false`.
 * @property {string} preset - Named prop preset from nearest `sky-config-provider`. Default: `""`.
 * @method check Sets checked state to `true`.
 * @method uncheck Sets checked state to `false`.
 * @method toggle Toggles checked state.
 * @method reset Resets checkbox state to defaults and clears UI validation state.
 *
 * @fires {CustomEvent<SkyCheckboxValueChangedDetail>} value-changed - Fired when checked state changes; `detail.checked` is always boolean (use for v-model). `detail.value` is the form token when the `value` prop is set, else mirrors `checked`.
 * @fires {CustomEvent<SkyCheckboxValidationErrorDetail>} validation-error - Fired when validation errors are updated.
 * @fires {CustomEvent<SkyCheckboxValueClearedDetail>} value-cleared - Fired when checkbox is reset programmatically.
 *
 * @example
 * ```html
 * <sky-checkbox label="Accept Terms" required showerrors prefix="Legal"></sky-checkbox>
 * ```
 * ```vue
 * <template>
 *   <sky-checkbox label="Accept Terms" required showerrors prefix="Legal"></sky-checkbox>
 * </template>
 * ```
 * ```jsx
 * export default function Demo() {
 *   return <sky-checkbox label="Accept Terms" required showerrors prefix="Legal"></sky-checkbox>;
 * }
 * ```
 */
@customElement("sky-checkbox")
export class SkyCheckbox extends FormControlBase {
  static override shadowRootOptions = {
    ...LitElement.shadowRootOptions,
    delegatesFocus: true,
  };

  @property({ type: String }) label = "";
  @property({ type: String }) override id = "";
  @property({ type: String }) value = "";
  @property({ type: String }) color = "primary";
  @property({ type: Boolean }) defaultChecked = false;
  @property({ type: Boolean }) showErrors = false;
  @property({ type: String }) override prefix = "";
  @property({ type: String }) suffix = "";
  @property({ type: String }) prefixColor = "";
  @property({ type: String }) suffixColor = "";
  @property({ type: Boolean, reflect: true }) validationActive: boolean = false;
  @property({ type: Boolean, reflect: true }) invalid: boolean = false;
  @state() private _checked = false;
  @state() private _indeterminate = false;
  @state() private validationErrors: string[] = [];
  @state() private _hasMeaningfulSlot = false;

  private _inputElement?: HTMLInputElement;
  private _inputId = `sky-cb-${Math.random().toString(36).slice(2)}`;

  @property({ type: Boolean, reflect: true })
  get checked() {
    return this._checked;
  }
  set checked(v: boolean) {
    const old = this._checked;
    this._checked = v;

    if (v && this.indeterminate) {
      this.indeterminate = false;
      if (this._inputElement) this._inputElement.indeterminate = false;
    }

    this.requestUpdate("checked", old);

    if (this._inputElement) {
      this._inputElement.checked = v;
      this._inputElement.indeterminate = this.indeterminate;
    }

    this._updateVisualState();

    this.syncFormValue();
    this.syncValidity();
  }

  @property({ type: Boolean, reflect: true })
  get indeterminate() {
    return this._indeterminate;
  }
  set indeterminate(v: boolean) {
    const old = this._indeterminate;
    this._indeterminate = v;

    if (v && this.checked) this.checked = false;

    this.requestUpdate("indeterminate", old);

    if (this._inputElement) this._inputElement.indeterminate = v;

    this._updateVisualState();

    this.syncValidity();
  }


  /** Named prop preset from nearest `sky-config-provider`. */
  @property({ type: String, reflect: true }) preset = "";

  private _presets = new PresetController(this);

  static override styles = [
    skyFocusRingHostStyles,
    css`
    :host {
      display: block;
      position: relative;
      font-family: var(--sky-font);
      /* Tint focus ring with the control color (not global active-primary). */
      --sky-focus-ring-color: var(--checkbox-color, var(--sky-active-primary));
    }

    :host([readonly]) .cbx {
      cursor: default;
    }

    /* Show focus UI whenever anything inside is focused */
    :host(:focus-within) .cbx span:first-child,
    :host(:focus) .cbx span:first-child {
      box-shadow: var(--sky-focus-ring);
      border-color: var(--checkbox-color, var(--sky-active-primary)) !important;
    }

    .checkbox-wrapper {
      position: relative;
      display: flex;
      align-items: center;
      gap: var(--sky-space-1, 5px);
    }

    :host([disabled]) {
      pointer-events: none;
      opacity: var(--sky-opacity-disabled, 0.6);
    }

    .cbx {
      display: flex;
      align-items: center;
      cursor: pointer;
      user-select: none;
      gap: var(--sky-space-1, 5px);
      transition: color 0.2s ease;
      font-family: var(--sky-font);
      font-size: var(--sky-font-size-sm);
      line-height: var(--sky-line-height-snug, 1.25);
      color: var(--sky-text-primary);
    }

    .cbx span {
      display: inline-block;
      vertical-align: middle;
    }

    .cbx span:first-child {
      position: relative;
      box-sizing: border-box;
      width: 18px;
      height: 18px;
      flex-shrink: 0;
      border-radius: min(6px, var(--sky-radius-primary));
      vertical-align: middle;
      background: var(--sky-glass-solid);
      border: 2px solid var(--sky-text-tertiary);
      display: flex;
      align-items: center;
      justify-content: center;
      transform: translateZ(0);
      contain: layout;
      /* visible so --sky-focus-ring (outer box-shadow) is not clipped */
      overflow: visible;
      transition: background-color 0.2s ease, border-color 0.2s ease,
        box-shadow 0.2s ease;
    }

    .cbx span:first-child svg {
      position: absolute;
      width: 12px;
      height: 10px;
      fill: none;
      stroke: var(--sky-on-color-fallback-light, var(--sky-text-white));
      stroke-width: 2;
      stroke-linecap: round;
      stroke-linejoin: round;
      stroke-dasharray: 16px;
      stroke-dashoffset: 16px;
      transition: stroke-dashoffset 0.2s ease, transform 0.2s ease;
      transform: translate3d(0, 0, 0);
    }

    .cbx:hover span:first-child {
      background-color: var(--sky-hover-primary);
      border-color: var(--sky-text-tertiary);
    }

    input[type="checkbox"]:checked + .cbx:hover span:first-child {
      box-shadow: var(--sky-box-shadow-hover);
      transition: box-shadow 0.2s ease;
    }

    /* Keep focus ring above hover shadow when both apply */
    :host(:focus-within) input[type="checkbox"]:checked + .cbx:hover span:first-child,
    :host(:focus) input[type="checkbox"]:checked + .cbx:hover span:first-child {
      box-shadow: var(--sky-focus-ring);
    }

    .cbx span:first-child:before {
      content: "";
      inset: 0;
      width: auto;
      height: auto;
      background: var(--checkbox-color, var(--sky-active-primary));
      display: block;
      transform: scale(0);
      opacity: 0;
      border-radius: 50%;
      transition: transform 0.2s ease-out, opacity 0.2s ease-out;
      position: absolute;
      z-index: -1;
      pointer-events: none;
    }

    input[type="checkbox"] {
      position: absolute;
      opacity: 0;
      width: 1px;
      height: 1px;
      margin: 0;
      padding: 0;
      border: 0;
    }

    input[type="checkbox"]:focus-visible + .cbx span:first-child,
    input[type="checkbox"]:focus + .cbx span:first-child {
      outline: none;
    }

    input[type="checkbox"]:checked + .cbx span:first-child {
      background: var(--checkbox-color, var(--sky-active-primary));
      border-color: var(--checkbox-color, var(--sky-active-primary));
      transform: none;
    }

    input[type="checkbox"]:checked + .cbx span:first-child svg.checkmark {
      stroke-dashoffset: 0;
      transition: stroke-dashoffset 0.2s ease-in-out 0.1s,
        transform 0.2s ease-in-out 0.1s;
    }

    input[type="checkbox"]:not(:checked) + .cbx span:first-child svg.checkmark {
      transition: opacity 0.2s ease-in-out;
      opacity: 0;
    }

    input[type="checkbox"]:checked + .cbx span:first-child:before {
      transform: scale(3.5);
      opacity: 0;
      transition: transform 0.2s ease-out, opacity 0.2s ease-out;
    }

    .cbx.checked span:first-child {
      background: var(--checkbox-color, var(--sky-active-primary));
      border-color: var(--checkbox-color, var(--sky-active-primary));
    }

    .cbx.checked span:first-child svg.checkmark {
      stroke-dashoffset: 0;
      transition: stroke-dashoffset 0.2s ease-in-out 0.1s,
        transform 0.2s ease-in-out 0.1s;
    }

    .indeterminate-line {
      width: 10px;
      height: 2px;
      background-color: var(--sky-on-color-fallback-light, var(--sky-text-white));
      position: absolute;
      border-radius: 1px;
      transition: opacity 0.2s ease, transform 0.2s ease;
      opacity: 0;
      transform: scaleX(0);
    }

    .indeterminate-active {
      opacity: 1;
      transform: scaleX(1);
    }

    .checked .checkmark {
      stroke-dashoffset: 0;
    }

    .indeterminate .checkmark {
      display: none;
    }

    .indeterminate .indeterminate-line {
      opacity: 1;
      transform: scaleX(1);
    }


    .error-message {
      font-size: var(--sky-font-size-xs);
      color: var(--sky-danger-primary);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      pointer-events: none;
      margin-left: var(--sky-space-1, 5px);
      width: 100%;
      text-align: start;
      font-family: var(--sky-font);
    }
    :dir(rtl) .error-message {
      margin-right: var(--sky-space-1, 5px);
      margin-left: unset;
    }

    :host([validationactive]:invalid) .cbx span:first-child {

      border-color: var(--sky-danger-primary);
    }
    :host([validationactive]:invalid) .cbx span:first-child svg.checkmark {
      stroke-dashoffset: 0;
    }
    :host([validationactive]:invalid) .cbx span:first-child:before {
      transform: scale(3.5);
      opacity: 0;
    }
    :host([validationactive]:invalid) .error-message {
      display: block;
    }

    :host([indeterminate]) .indeterminate-line {
      opacity: 1;
      transform: scaleX(1);
    }
    :host([indeterminate]) .cbx span:first-child {
      background-color: var(--checkbox-color);
      border-color: var(--checkbox-color);
    }

    .checkbox-wrapper.no-text {
      gap: 0;
    }

    @media (prefers-reduced-motion: reduce) {
      .cbx,
      .cbx span:first-child,
      .cbx span:first-child svg,
      .cbx span:first-child:before,
      .indeterminate-line {
        transition: none;
      }
    }

    ${unsafeCSS(
      cssOnFillEnhance([
        {
          selector:
            'input[type="checkbox"]:checked + .cbx span:first-child svg, .cbx.checked span:first-child svg',
          fillVar: "var(--checkbox-color, var(--sky-active-primary))",
          property: "stroke",
        },
        {
          selector: ".indeterminate-line, .indeterminate .indeterminate-line",
          fillVar: "var(--checkbox-color, var(--sky-active-primary))",
          property: "background-color",
        },
      ]),
    )}
  `,
  ];

  protected override getFormValue(): FormState {
    if (!this.name) return null;
    if (this.disabled) return null;

    if (!this.checked) return null;

    if (this.value && this.value.trim() !== "") return this.value;

    return "on";
  }

  protected override setValueFromFormState(state: FormState): void {
    if (state === null) {
      this.checked = false;
      this.indeterminate = false;
      return;
    }

    if (typeof state === "string") {
      if (this.value && this.value.trim() !== "") {
        this.checked = state === this.value;
      } else {
        this.checked = state === "on" || state === "true" || state === "1";
      }
      this.indeterminate = false;
    }
  }

  protected override getValidityAnchor(): HTMLElement | undefined {
    return (this.shadowRoot?.querySelector('input[type="checkbox"]') as HTMLElement) ?? undefined;
  }

  protected override isEmpty(): boolean {
    return !this.checked;
  }

  protected override getNativeControl(): HTMLElement | null {
    return this.shadowRoot?.querySelector('input[type="checkbox"]') as HTMLInputElement | null;
  }

  protected override onFormReset(): void {
    this.checked = this.defaultChecked;
    this.indeterminate = false;

    this.validationErrors = [];
    this.invalid = false;
    this.validationActive = false;
  }

  override updated(changed: Map<string, unknown>): void {
    super.updated(changed);

    // After PresetController hostUpdate — color may come from presets.
    this.style.setProperty("--checkbox-color", resolveColor(this.color));

    if (!this._inputElement) {
      this._inputElement = this.shadowRoot?.querySelector(
        'input[type="checkbox"]'
      ) as HTMLInputElement;
    }

    if (this._inputElement) {
      this._inputElement.checked = this.checked;
      this._inputElement.indeterminate = this.indeterminate;
      this._inputElement.disabled = this.disabled || this.readonly;
    }

    this._updateVisualState();

    if (
      changed.has("checked") ||
      changed.has("name") ||
      changed.has("disabled") ||
      changed.has("readonly") ||
      changed.has("required") ||
      changed.has("value")
    ) {
      this.syncFormValue();
      this.syncValidity();
    }
  }

  private handleChange(event: Event): void {
    const target = event.target as HTMLInputElement;

    if (this.disabled || this.readonly) {
      if (this._inputElement) {
        this._inputElement.checked = this.checked;
        this._inputElement.indeterminate = this.indeterminate;
      }
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    this.checked = target.checked;

    this.validateInput();

    const emittedValue =
      this.value && this.value.trim() !== ""
        ? this.checked ? this.value : undefined
        : this.checked;

    this.dispatchEvent(
      new CustomEvent<SkyCheckboxValueChangedDetail>("value-changed", {
        detail: { checked: this.checked, value: emittedValue },
        bubbles: true,
        composed: true,
      })
    );
  }

  private validateInput(): boolean {
    if (!this.validationActive) return true;

    this.validationErrors = [];
    if (this.required && !this.checked) {
      this.validationErrors = [this.requiredMessage]; // from base
    }

    this.dispatchEvent(
      new CustomEvent<SkyCheckboxValidationErrorDetail>("validation-error", {
        detail: { errors: [...this.validationErrors] },
        bubbles: true,
        composed: true,
      })
    );

    const cbx = this.shadowRoot?.querySelector(".cbx") as HTMLElement | null;
    if (this.validationErrors.length) cbx?.classList.add("invalid");
    else cbx?.classList.remove("invalid");

    const isValid = this.validationErrors.length === 0;
    this.invalid = !isValid;

    return isValid;
  }

  /**
   * Sets checked state to `true`.
   *
   * @returns {void}
   */
  public check(): void {
    if (this.disabled || this.readonly) return;
    this.checked = true;
    this.indeterminate = false;
  }

  /**
   * Sets checked state to `false`.
   *
   * @returns {void}
   */
  public uncheck(): void {
    if (this.disabled || this.readonly) return;
    this.checked = false;
    this.indeterminate = false;
  }

  /**
   * Toggles checked state.
   *
   * @returns {void}
   */
  public toggle(): void {
    if (this.disabled || this.readonly) return;
    this.checked = !this.checked;
    this.indeterminate = false;
  }

  /**
   * Resets checkbox state to defaults and clears UI validation state.
   *
   * @returns {void}
   */
  public reset(): void {
    this.onFormReset();
    this.syncFormValue();
    this.syncValidity();

    this.dispatchEvent(
      new CustomEvent<SkyCheckboxValueClearedDetail>("value-cleared", {
        detail: { value: this.value, checked: this.checked },
        bubbles: true,
        composed: true,
      })
    );
  }

  private _updateVisualState(): void {
    const cbx = this.shadowRoot?.querySelector(".cbx");
    if (!cbx) return;

    if (this.checked) {
      cbx.classList.add("checked");
      cbx.classList.remove("indeterminate");
    } else if (this.indeterminate) {
      cbx.classList.add("indeterminate");
      cbx.classList.remove("checked");
    } else {
      cbx.classList.remove("checked", "indeterminate");
    }
  }

  private _slotHasMeaningfulContent(slotEl: HTMLSlotElement): boolean {
    const nodes = slotEl.assignedNodes({ flatten: true });
    return nodes.some((n) =>
      n.nodeType === Node.ELEMENT_NODE
        ? true
        : (n.textContent ?? "").trim().length > 0
    );
  }

  private _onSlotChange = (e: Event) => {
    const slot = e.target as HTMLSlotElement;
    const next = this._slotHasMeaningfulContent(slot);
    if (next !== this._hasMeaningfulSlot) this._hasMeaningfulSlot = next;
  };

  private get _hasTextContent(): boolean {
    return !!this.prefix || !!this.suffix || !!this.label || this._hasMeaningfulSlot;
  }

  private handleBlur = (_e: FocusEvent): void => {
    this.validationActive = true;
    this.validateInput();

    const cbx = this.shadowRoot?.querySelector(".cbx");
    cbx?.classList.remove("active");

    this.syncValidity();
  };
  private _onKeyDown = (e: KeyboardEvent): void => {
    if (this.disabled || this.readonly) return;

    if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      this._inputElement?.click();
    }
  };


  override render() {
    const hasText = this._hasTextContent;
    const wrapperClasses = classMap({
      "checkbox-wrapper": true,
      "no-text": !hasText,
    });
    const labelClasses = classMap({
      cbx: true,
      checked: this.checked,
      indeterminate: this.indeterminate,
    });
    const boxClasses = classMap({
      box: true,
      "box--checked": this.checked,
      "box--indeterminate": this.indeterminate,
      "box--invalid": this.validationErrors.length > 0,
    });
    const boxPart =
      "box" +
      (this.checked ? " box--checked" : "") +
      (this.indeterminate ? " box--indeterminate" : "") +
      (this.validationErrors.length ? " box--invalid" : "");

    return html`
      <div class=${wrapperClasses} part="wrapper">
        <input
          type="checkbox"
          id="${this._inputId}"
          class="inp-cbx"
          part="input"
          .checked="${this.checked}"
          @blur=${this.handleBlur}
          @keydown=${this._onKeyDown}
          .disabled="${this.disabled || this.readonly}"
          .value="${this.value}"
          @change=${this.handleChange}
          aria-checked=${this.indeterminate ? "mixed" : this.checked}
          aria-invalid="${this.validationErrors.length > 0}"
          aria-readonly="${this.readonly}"
        />
        <label class=${labelClasses} part="label" for="${this._inputId}">
          <span part=${boxPart} class=${boxClasses}>
            <svg viewBox="0 0 12 10" class="checkmark" part="checkmark">
              <polyline points="1.5 6 4.5 9 10.5 1"></polyline>
            </svg>
            <div class="indeterminate-line" part="indeterminate-line"></div>
          </span>
          <slot @slotchange=${this._onSlotChange}></slot>
          ${when(
            hasText,
            () => {
              const textStyle = styleMap({ textAlign: "start", color: "var(--sky-text-primary)" });
              const prefixStyle = styleMap({ color: this.prefixColor, display: "inline-block" });
              const suffixStyle = styleMap({ color: this.suffixColor, display: "inline-block" });
              return html`
              <span style=${textStyle} part="text">
                <div style=${prefixStyle} part="prefix">
                  ${this.prefix}
                </div>
                ${this._hasMeaningfulSlot ? nothing : this.label}
                <div style=${suffixStyle} part="suffix">
                  ${this.suffix}
                </div>
              </span>
            `;
            },
          )}
        </label>
      </div>
      ${when(
        this.showErrors &&
          this.validationErrors.length > 0 &&
          this.validationActive,
        () => html`<div class="error-message" part="error-message">
          ${this.validationErrors[0]}
        </div>`,
      )}
    `;
  }
}